import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { asapScheduler, EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, finalize, map, observeOn, reduce, shareReplay, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { CountryContextService } from '@app/core/services/country-context.service';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import {
  CreateAgentAccountRequest,
  CreatePartnerRequest,
  PartnerModel,
  PartnerStatus,
  SearchPartnersRequest,
  UpdatePartnerRequest,
} from '@app/core/models/partner/partner.model';
import { PartnerEmployeeModel } from '@app/core/models/partner/partner-employee.model';
import { backendErrorText } from '@app/core/utils/backend-error';

const EMPTY_PARTNERS_PAGE: PaginatedResponse<PartnerModel> = {
  currentPageNumber: 0,
  pageSize: 0,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

/** Бэкенд отклоняет size > 200 ("Размер страницы от 1 до 200") для списка и поиска партнеров */
const MAX_PAGE_SIZE = 200;

/**
 * Full partner lists (all pages, 200 per request) take seconds and are requested by most pages and
 * dialogs, so they are cached. Any partner change made through this service clears the cache;
 * the TTL picks up changes made by other admins.
 */
const LIST_CACHE_TTL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly countryContext = inject(CountryContextService);

  readonly isLoading = signal(false);

  private readonly listCache = new Map<string, { expiresAt: number; data$: Observable<unknown> }>();

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/admin/partners`;
  }

  getPartners(): Observable<PartnerModel[]> {
    this.isLoading.set(true);

    const key = 'all';
    return this.cachedListAsync(key, () =>
      this.fetchAllPages((page, size) =>
        this.http
          .get<PartnerModel[] | PaginatedResponse<PartnerModel>>(this.baseUrl, {
            params: new HttpParams().set('page', String(page)).set('size', String(size)),
          })
          .pipe(map(response => this.toPage(response)))
      )
    ).pipe(
      map(response => [...response.content]),
      catchError(error => {
        this.dropCachedList(key);
        console.error('Ошибка при получении списка партнеров:', error);
        this.translate.get('Ошибка при получении списка партнеров').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  searchPartners(body: SearchPartnersRequest): Observable<PartnerModel[]> {
    this.isLoading.set(true);
    return this.http.post<PartnerModel[]>(`${this.baseUrl}/search`, body).pipe(
      catchError(error => {
        console.error('Ошибка при поиске партнеров:', error);
        this.translate.get('Ошибка при поиске партнеров').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /**
   * Страница поиска партнеров. Если запрошено больше 200 записей (списки для выпадающих
   * списков передают 1000), загружаем все страницы по 200 и склеиваем в одну.
   */
  searchPartnersPage(
    page: number,
    size: number,
    body: SearchPartnersRequest
  ): Observable<PaginatedResponse<PartnerModel>> {
    this.isLoading.set(true);

    const fetchPage = (p: number, s: number) =>
      this.http.post<PaginatedResponse<PartnerModel>>(`${this.baseUrl}/search`, body, {
        params: new HttpParams().set('page', String(p)).set('size', String(s)),
      });

    // Only the "everything" mode (dropdowns ask for 1000) is cached; real pages always hit the API.
    const key = `search:${JSON.stringify(body)}`;
    const request$ =
      size > MAX_PAGE_SIZE
        ? this.cachedListAsync(key, () => this.fetchAllPages(fetchPage)).pipe(
            map(result => ({ ...result, content: [...(result.content ?? [])] }))
          )
        : fetchPage(page, size);

    return request$.pipe(
      catchError(error => {
        this.dropCachedList(key);
        console.error('Ошибка при поиске партнеров (page):', error);
        this.translate.get('Ошибка при поиске партнеров').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(EMPTY_PARTNERS_PAGE);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /**
   * @param trackLoading false — не трогать общий isLoading. Карточка партнера на время isLoading
   * целиком заменяется спиннером, и вложенные компоненты (вкладки маркетинга) уничтожаются
   * вместе со своими запросами.
   */
  getPartnerById(partnerId: string, trackLoading = true): Observable<PartnerModel | null> {
    if (!partnerId) return of(null);
    if (trackLoading) this.isLoading.set(true);
    return this.http.get<PartnerModel>(`${this.baseUrl}/${partnerId}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении партнера:', error);
        this.translate.get('Ошибка при получении партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => {
        if (trackLoading) this.isLoading.set(false);
      })
    );
  }

  createPartner(body: CreatePartnerRequest): Observable<PartnerModel> {
    this.isLoading.set(true);
    return this.http.post<PartnerModel>(this.baseUrl, body).pipe(
      tap(() => this.invalidateListCache()),
      catchError(error => {
        console.error('Ошибка при создании партнера:', error);
        // Текст бэкенда (занятый ИИН, ошибки полей) показывает форма — под полями
        if (!this.hasBackendMessage(error)) {
          this.translate.get('Ошибка при создании партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  updatePartner(partnerId: string, body: UpdatePartnerRequest): Observable<PartnerModel> {
    this.isLoading.set(true);
    return this.http.put<PartnerModel>(`${this.baseUrl}/${partnerId}`, body).pipe(
      tap(() => this.invalidateListCache()),
      catchError(error => {
        console.error('Ошибка при обновлении партнера:', error);
        if (!this.hasBackendMessage(error)) {
          this.translate.get('Ошибка при обновлении партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deletePartner(partnerId: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${partnerId}`).pipe(
      tap(() => this.invalidateListCache()),
      catchError(error => {
        console.error('Ошибка при удалении партнера:', error);
        this.translate.get('Ошибка при удалении партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /** Активировать/деактивировать карточку партнера целиком */
  setPartnerStatus(partnerId: string, status: PartnerStatus): Observable<PartnerModel> {
    this.isLoading.set(true);
    return this.http.patch<PartnerModel>(`${this.baseUrl}/${partnerId}/status`, { status }).pipe(
      tap(() => this.invalidateListCache()),
      catchError(error => {
        console.error('Ошибка при изменении статуса партнера:', error);
        this.translate.get('Не удалось изменить статус партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /** Включить/отключить возможность входа учетной записи агента */
  setAgentAccountStatus(agentId: string, active: boolean): Observable<PartnerEmployeeModel> {
    this.isLoading.set(true);
    return this.http
      .patch<PartnerEmployeeModel>(`${this.baseUrl}/${agentId}/account/status`, { active })
      .pipe(
        tap(() => this.invalidateListCache()),
        catchError(error => {
          console.error('Ошибка при изменении доступа агента:', error);
          // При включении бэкенд проверяет агента, компанию, сеть и магазин — причину показываем как есть
          this.translate.get('Не удалось изменить доступ агента').subscribe(msg => {
            this.snackbarService.error(backendErrorText(error) ?? msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  /** Учетная запись агента для входа (роль PARTNER_AGENT). null — учетной записи еще нет */
  getAgentAccount(agentId: string): Observable<PartnerEmployeeModel | null> {
    return this.http
      .get<PartnerEmployeeModel>(`${this.baseUrl}/${agentId}/account`)
      .pipe(catchError(() => of(null)));
  }

  /** Создать агенту пользователя для входа в систему с ролью PARTNER_AGENT */
  createAgentAccount(agentId: string, body: CreateAgentAccountRequest): Observable<PartnerEmployeeModel> {
    this.isLoading.set(true);
    return this.http.post<PartnerEmployeeModel>(`${this.baseUrl}/${agentId}/account`, body).pipe(
      tap(() => this.invalidateListCache()),
      catchError(error => {
        console.error('Ошибка при создании учетной записи агента:', error);
        // 409 (EMAIL_EXIST, аккаунт уже есть) показывает диалог — под конкретным полем
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        if (status !== 409) {
          const key =
            status === 404
              ? 'partners.agentAccount.agentNotFound'
              : status === 400
                ? 'partners.agentAccount.agentInactive'
                : 'partners.agentAccount.createFailed';
          this.translate.get(key).subscribe(msg => {
            this.snackbarService.error(status === 400 ? (backendErrorText(error) ?? msg) : msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /**
   * Изменить процент вознаграждения для будущих покупок (старые операции не пересчитываются).
   * null — сбросить процент партнера.
   */
  // Без общего isLoading: вызывается из вкладки «Вознаграждение» внутри карточки партнера,
  // иначе карточка показывает спиннер, вкладка уничтожается и PUT отменяется (canceled).
  updateRewardSettings(partnerId: string, rewardPercent: number | null): Observable<PartnerModel> {
    return this.http
      .put<PartnerModel>(`${this.baseUrl}/${partnerId}/reward-settings`, { rewardPercent })
      .pipe(
        tap(() => this.invalidateListCache()),
        catchError(error => {
          console.error('Ошибка при изменении процента вознаграждения:', error);
          this.translate.get('Не удалось изменить процент вознаграждения').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        })
      );
  }

  /** Ответ с текстом ошибки от бэкенда ({ message } или { fieldErrors }) — его показывает форма */
  /** Forget cached partner lists, e.g. after a partner was created somewhere else. */
  invalidateListCache(): void {
    this.listCache.clear();
  }

  private cachedList<T>(key: string, load: () => Observable<T>): Observable<T> {
    const fullKey = `${this.countryContext.selectedCountryId()}|${key}`;
    const hit = this.listCache.get(fullKey);
    if (hit && hit.expiresAt > Date.now()) return hit.data$ as Observable<T>;

    const data$ = load().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.listCache.set(fullKey, { expiresAt: Date.now() + LIST_CACHE_TTL_MS, data$ });
    return data$;
  }

  /**
   * Cached lists are replayed asynchronously, like a real HTTP response: several pages call
   * getPartners() before subscribing to their own controls and would miss a synchronous emission.
   */
  private cachedListAsync<T>(key: string, load: () => Observable<T>): Observable<T> {
    return this.cachedList(key, load).pipe(observeOn(asapScheduler));
  }

  /** A failed load must not stay cached as an empty list. */
  private dropCachedList(key: string): void {
    this.listCache.delete(`${this.countryContext.selectedCountryId()}|${key}`);
  }

  private hasBackendMessage(error: unknown): boolean {
    const body = error instanceof HttpErrorResponse ? (error.error as Record<string, unknown> | null) : null;
    return !!body && typeof body === 'object' && (!!body['message'] || !!body['fieldErrors']);
  }

  /** Список партнеров бэкенд может отдать массивом — приводим к одной «последней» странице */
  private toPage<T>(response: T[] | PaginatedResponse<T>): PaginatedResponse<T> {
    if (!Array.isArray(response)) return response;
    return {
      currentPageNumber: 0,
      pageSize: response.length,
      totalElements: response.length,
      totalPages: 1,
      isLastPage: true,
      isFirstPage: true,
      isEmpty: response.length === 0,
      content: response,
    };
  }

  /** Последовательно загружает все страницы по MAX_PAGE_SIZE и склеивает их в одну */
  private fetchAllPages<T>(
    fetchPage: (page: number, size: number) => Observable<PaginatedResponse<T>>
  ): Observable<PaginatedResponse<T>> {
    return fetchPage(0, MAX_PAGE_SIZE).pipe(
      expand(res => {
        const next = Number(res?.currentPageNumber) + 1;
        const totalPages = Number(res?.totalPages);
        // Останавливаемся и на ответе без полей пагинации — иначе запросы страницы NaN пойдут без конца
        const done =
          res?.isLastPage === true ||
          !Number.isFinite(next) ||
          !Number.isFinite(totalPages) ||
          next >= totalPages ||
          !(res.content ?? []).length;
        return done ? EMPTY : fetchPage(next, MAX_PAGE_SIZE);
      }),
      reduce(
        (all, res) => ({
          ...res,
          currentPageNumber: 0,
          pageSize: all.content.length + (res.content ?? []).length,
          totalPages: 1,
          isFirstPage: true,
          isLastPage: true,
          content: [...all.content, ...(res.content ?? [])],
        }),
        { ...EMPTY_PARTNERS_PAGE, content: [] as T[] } as PaginatedResponse<T>
      ),
      map(res => ({ ...res, isEmpty: res.content.length === 0 }))
    );
  }
}
